import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Building2,
  Search,
  RefreshCw,
  Eye,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronRight,
  Users,
  Folder,
  FileCheck,
  AlertCircle
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, AdminExchange } from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

interface Document {
  id: string
  name: string
  type: string
  size: number
  uploadDate: string
  status: 'pending' | 'processing' | 'verified' | 'rejected'
  category: string
  exchangeId?: string
  accountId?: string
  uploadedBy?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
}

interface AccountWithExchanges {
  id: string
  name: string
  documentCount: number
  exchanges: (AdminExchange & { documentCount: number; documents: Document[] })[]
  totalDocuments: number
}

export const AdminDocumentsPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<AccountWithExchanges[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set())
  const [expandedExchanges, setExpandedExchanges] = useState<Set<string>>(new Set())
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadDocuments()
  }, [currentUser, navigate])

  const loadDocuments = async () => {
    try {
      setIsLoading(true)
      
      // Mock data structure - replace with actual API call
      const mockAccounts: AccountWithExchanges[] = [
        {
          id: 'acc-1',
          name: 'Smith Family Trust',
          documentCount: 12,
          totalDocuments: 12,
          exchanges: [
            {
              id: 'ex-1',
              accountId: 'acc-1',
              name: 'Downtown Office Exchange',
              status: 'active',
              accountName: 'Smith Family Trust',
              userCount: 3,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              documentCount: 8,
              documents: [
                {
                  id: 'doc-1',
                  name: 'Exchange_Agreement.pdf',
                  type: 'application/pdf',
                  size: 1048576,
                  uploadDate: new Date().toISOString(),
                  status: 'verified',
                  category: 'agreement',
                  exchangeId: 'ex-1',
                  accountId: 'acc-1',
                  uploadedBy: {
                    id: 'user-1',
                    firstName: 'John',
                    lastName: 'Smith',
                    email: 'john@example.com'
                  }
                },
                {
                  id: 'doc-2',
                  name: 'Purchase_Agreement_Relinquished.pdf',
                  type: 'application/pdf',
                  size: 2097152,
                  uploadDate: new Date().toISOString(),
                  status: 'verified',
                  category: 'purchase_agreement',
                  exchangeId: 'ex-1',
                  accountId: 'acc-1'
                },
                {
                  id: 'doc-3',
                  name: 'W9_Tax_ID.pdf',
                  type: 'application/pdf',
                  size: 262144,
                  uploadDate: new Date().toISOString(),
                  status: 'pending',
                  category: 'tax',
                  exchangeId: 'ex-1',
                  accountId: 'acc-1'
                }
              ]
            },
            {
              id: 'ex-2',
              accountId: 'acc-1',
              name: 'Residential Property Exchange',
              status: 'completed',
              accountName: 'Smith Family Trust',
              userCount: 2,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              documentCount: 4,
              documents: [
                {
                  id: 'doc-4',
                  name: 'Exchange_Agreement.pdf',
                  type: 'application/pdf',
                  size: 1048576,
                  uploadDate: new Date().toISOString(),
                  status: 'verified',
                  category: 'agreement',
                  exchangeId: 'ex-2',
                  accountId: 'acc-1'
                }
              ]
            }
          ]
        },
        {
          id: 'acc-2',
          name: 'Johnson Investments LLC',
          documentCount: 5,
          totalDocuments: 5,
          exchanges: [
            {
              id: 'ex-3',
              accountId: 'acc-2',
              name: 'Commercial Building Exchange',
              status: 'active',
              accountName: 'Johnson Investments LLC',
              userCount: 1,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              documentCount: 5,
              documents: [
                {
                  id: 'doc-5',
                  name: 'Exchange_Agreement.pdf',
                  type: 'application/pdf',
                  size: 1048576,
                  uploadDate: new Date().toISOString(),
                  status: 'verified',
                  category: 'agreement',
                  exchangeId: 'ex-3',
                  accountId: 'acc-2'
                }
              ]
            }
          ]
        }
      ]

      // Filter by search query
      let filtered = mockAccounts
      if (searchQuery) {
        filtered = mockAccounts.map(account => ({
          ...account,
          exchanges: account.exchanges.map(exchange => ({
            ...exchange,
            documents: exchange.documents.filter(doc =>
              doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              doc.category.toLowerCase().includes(searchQuery.toLowerCase())
            )
          })).filter(exchange => exchange.documents.length > 0)
        })).filter(account => account.exchanges.length > 0)
      }

      setAccounts(filtered)
    } catch (error) {
      console.error('Failed to load documents:', error)
      toast.error('Failed to load documents')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleAccount = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts)
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId)
    } else {
      newExpanded.add(accountId)
    }
    setExpandedAccounts(newExpanded)
  }

  const toggleExchange = (exchangeId: string) => {
    const newExpanded = new Set(expandedExchanges)
    if (newExpanded.has(exchangeId)) {
      newExpanded.delete(exchangeId)
    } else {
      newExpanded.add(exchangeId)
    }
    setExpandedExchanges(newExpanded)
  }

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document)
  }

  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return
    }

    try {
      // Mock delete - replace with actual API call
      toast.success('Document deleted successfully')
      loadDocuments()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete document')
    }
  }

  const handleApproveDocument = async (documentId: string) => {
    try {
      // Mock approve - replace with actual API call
      toast.success('Document approved successfully')
      loadDocuments()
    } catch (error: any) {
      toast.error('Failed to approve document')
    }
  }

  const handleRejectDocument = async (documentId: string) => {
    try {
      // Mock reject - replace with actual API call
      toast.success('Document rejected')
      loadDocuments()
    } catch (error: any) {
      toast.error('Failed to reject document')
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Verified
          </span>
        )
      case 'processing':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Processing
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        )
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (isLoading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const totalDocuments = accounts.reduce((sum, acc) => sum + acc.totalDocuments, 0)
  const totalExchanges = accounts.reduce((sum, acc) => sum + acc.exchanges.length, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary-600" />
            Document Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage documents across all accounts and exchanges
          </p>
        </div>
        <Button onClick={loadDocuments} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Accounts</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-2">{accounts.length}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Exchanges</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-2">{totalExchanges}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Documents</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-2">{totalDocuments}</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search documents by name or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Accounts List */}
      <div className="space-y-4">
        {accounts.map((account) => (
          <Card key={account.id} className="overflow-hidden">
            {/* Account Header */}
            <div
              className="p-6 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              onClick={() => toggleAccount(account.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    {expandedAccounts.has(account.id) ? (
                      <ChevronDown className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Folder className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {account.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {account.exchanges.length} exchange{account.exchanges.length !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {account.totalDocuments} document{account.totalDocuments !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Exchanges List */}
            {expandedAccounts.has(account.id) && (
              <div className="border-t border-slate-200 dark:border-slate-700">
                {account.exchanges.map((exchange) => (
                  <div key={exchange.id}>
                    {/* Exchange Header */}
                    <div
                      className="p-4 pl-12 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800"
                      onClick={() => toggleExchange(exchange.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="p-1.5 bg-slate-100 dark:bg-slate-700 rounded">
                            {expandedExchanges.has(exchange.id) ? (
                              <ChevronDown className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                              <h4 className="font-medium text-slate-900 dark:text-slate-100">
                                {exchange.name}
                              </h4>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                exchange.status === 'active'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  : exchange.status === 'completed'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                              }`}>
                                {exchange.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {exchange.documentCount} document{exchange.documentCount !== 1 ? 's' : ''}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {exchange.userCount || 0} user{exchange.userCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents List */}
                    {expandedExchanges.has(exchange.id) && (
                      <div className="bg-slate-50 dark:bg-slate-900/50">
                        {exchange.documents.length > 0 ? (
                          <div className="divide-y divide-slate-200 dark:divide-slate-700">
                            {exchange.documents.map((document) => (
                              <div
                                key={document.id}
                                className="p-4 pl-20 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="p-2 bg-white dark:bg-slate-700 rounded-lg">
                                      <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                        {document.name}
                                      </p>
                                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                        <span>{formatFileSize(document.size)}</span>
                                        <span>{new Date(document.uploadDate).toLocaleDateString()}</span>
                                        {document.uploadedBy && (
                                          <span>
                                            {document.uploadedBy.firstName} {document.uploadedBy.lastName}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {getStatusBadge(document.status)}
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleViewDocument(document)}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        {document.status === 'pending' && (
                                          <>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => handleApproveDocument(document.id)}
                                              className="text-green-600 hover:text-green-700"
                                            >
                                              <CheckCircle className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => handleRejectDocument(document.id)}
                                              className="text-red-600 hover:text-red-700"
                                            >
                                              <XCircle className="h-4 w-4" />
                                            </Button>
                                          </>
                                        )}
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleDeleteDocument(document.id)}
                                          className="text-red-600 hover:text-red-700"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-8 pl-20 text-center text-slate-500 dark:text-slate-400">
                            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>No documents in this exchange</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {accounts.length === 0 && (
        <Card className="p-12 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
            No documents found
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            {searchQuery ? 'Try adjusting your search criteria.' : 'No accounts or documents available.'}
          </p>
        </Card>
      )}

      {/* Document Details Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Document Details
                </h2>
                <Button variant="outline" onClick={() => setSelectedDocument(null)}>
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Name</label>
                  <p className="text-slate-900 dark:text-slate-100">{selectedDocument.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Type</label>
                    <p className="text-slate-900 dark:text-slate-100">{selectedDocument.type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Size</label>
                    <p className="text-slate-900 dark:text-slate-100">{formatFileSize(selectedDocument.size)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</label>
                    <div className="mt-2">{getStatusBadge(selectedDocument.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Category</label>
                    <p className="text-slate-900 dark:text-slate-100 capitalize">{selectedDocument.category.replace('_', ' ')}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Upload Date</label>
                  <p className="text-slate-900 dark:text-slate-100">
                    {new Date(selectedDocument.uploadDate).toLocaleString()}
                  </p>
                </div>
                {selectedDocument.uploadedBy && (
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Uploaded By</label>
                    <p className="text-slate-900 dark:text-slate-100">
                      {selectedDocument.uploadedBy.firstName} {selectedDocument.uploadedBy.lastName}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{selectedDocument.uploadedBy.email}</p>
                  </div>
                )}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                  <Button variant="outline" onClick={() => {
                    toast.info('Download functionality would be implemented here')
                  }}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  {selectedDocument.status === 'pending' && (
                    <>
                      <Button onClick={() => {
                        handleApproveDocument(selectedDocument.id)
                        setSelectedDocument(null)
                      }}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button variant="outline" onClick={() => {
                        handleRejectDocument(selectedDocument.id)
                        setSelectedDocument(null)
                      }} className="text-red-600 hover:text-red-700">
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
